import type { Editor } from "@tiptap/core";

/** Nearest ancestor that scrolls vertically, if any. */
function findScrollParent(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = getComputedStyle(node);
    if (
      (overflowY === "auto" ||
        overflowY === "scroll" ||
        overflowY === "overlay") &&
      node.scrollHeight > node.clientHeight + 1
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Bottom edge of sticky episode chrome (page bar + find panel) or dashboard
 * toolbar — matches must clear this, not just the viewport top.
 */
function topInsetPx(margin: number): number {
  const chrome = document.querySelector(".sm-chrome-sticky");
  if (chrome instanceof HTMLElement) {
    return chrome.getBoundingClientRect().bottom + margin;
  }
  const toolbar = document.querySelector(".sm-toolbar");
  if (toolbar instanceof HTMLElement) {
    return toolbar.getBoundingClientRect().bottom + margin;
  }
  return margin;
}

/**
 * Scroll the page (or nearest scroll parent) just enough so `el` is fully
 * visible. No-op when it already is — avoids the small upward jump from
 * ProseMirror/browser default scrollIntoView.
 */
export function ensureElementInView(el: HTMLElement, margin = 12): void {
  const rect = el.getBoundingClientRect();
  const topBound = topInsetPx(margin);
  const bottomBound = window.innerHeight - margin;

  let delta = 0;
  if (rect.top < topBound) {
    delta = rect.top - topBound;
  } else if (rect.bottom > bottomBound) {
    delta = rect.bottom - bottomBound;
  }
  if (Math.abs(delta) < 1) return;

  const scrollParent = findScrollParent(el);
  if (scrollParent) {
    scrollParent.scrollTop += delta;
  } else {
    window.scrollBy(0, delta);
  }
}

/** Same as ensureElementInView, but for a caret/selection band in viewport coords. */
export function ensureVerticalBandInView(
  top: number,
  bottom: number,
  fromEl: HTMLElement,
  margin = 12,
): void {
  const topBound = topInsetPx(margin);
  const bottomBound = window.innerHeight - margin;

  let delta = 0;
  if (top < topBound) {
    delta = top - topBound;
  } else if (bottom > bottomBound) {
    delta = bottom - bottomBound;
  }
  if (Math.abs(delta) < 1) return;

  const scrollParent = findScrollParent(fromEl);
  if (scrollParent) {
    scrollParent.scrollTop += delta;
  } else {
    window.scrollBy(0, delta);
  }
}

/**
 * Scroll so the editor's current selection (find highlight) sits below sticky
 * chrome. Falls back to the cue cell when coords are unavailable.
 */
export function scrollEditorSelectionIntoView(
  editor: Editor,
  margin = 12,
): void {
  if (editor.isDestroyed) return;
  const dom = editor.view.dom as HTMLElement;
  try {
    const { from, to } = editor.state.selection;
    const start = editor.view.coordsAtPos(from);
    const endPos = Math.max(from, to - 1);
    const end = editor.view.coordsAtPos(endPos);
    ensureVerticalBandInView(
      Math.min(start.top, end.top),
      Math.max(start.bottom, end.bottom),
      dom,
      margin,
    );
  } catch {
    const cell = dom.closest(".sm-cue-cell");
    if (cell instanceof HTMLElement) ensureElementInView(cell, margin);
  }
}

/** Run scroll now and again after layout (scene switch / PaperFit zoom). */
export function scrollEditorSelectionIntoViewSoon(
  editor: Editor,
  margin = 12,
): void {
  scrollEditorSelectionIntoView(editor, margin);
  requestAnimationFrame(() => {
    scrollEditorSelectionIntoView(editor, margin);
    requestAnimationFrame(() => {
      scrollEditorSelectionIntoView(editor, margin);
    });
  });
}
